import { sb, SUPABASE_URL } from '../supabase';
import { logAdminAction } from './adminAudit';
import { SUPPLIER_VERIFICATION_FIELD_LABELS } from './supplierOnboarding';

// Admin outreach to one supplier, from the verification screen.
//
// Two channels behind one call, because the second one is not ready yet and the
// screen must not care:
//
//   · EMAIL goes through the admin-send-email edge function. That function was
//     built for exactly this — admin JWT required, recipients resolved
//     server-side, template types allowlisted, 200-per-operation cap — and until
//     now NOTHING in the app called it. The engine was built and the button was
//     never fitted.
//
//   · WHATSAPP opens a wa.me link with the message pre-written, and the admin
//     presses send himself. It is a manual send, and it is deliberate: all
//     nineteen approved templates are for orders, offers, claims and auth codes,
//     and none of them is about verification. A new template needs Meta's
//     approval before a single automated message can go out. When that approval
//     lands, sendOutreach() routes 'whatsapp' through whatsapp-send and NOTHING
//     in the UI changes.
//
// Every send is written to audit_log, so "have we already chased this one, and
// when" is answerable on the next screen rather than from memory.

// The supplier-facing subset of what admin-send-email accepts. Its allowlist also
// carries nudge_trader_first_request, which is for buyers and has no business on a
// verification screen. Anything outside the allowlist is refused there, so offering
// it here would only produce a failed call.
export const OUTREACH_EMAIL_TYPES = Object.freeze([
  { key: 'nudge_supplier_upload_docs', ar: 'تذكير برفع الوثائق', en: 'Nudge: upload documents' },
  { key: 'nudge_confirm_email', ar: 'تذكير بتأكيد البريد', en: 'Nudge: confirm email' },
  { key: 'notice_supplier_under_review', ar: 'إشعار: ملفك تحت المراجعة', en: 'Notice: file under review' },
]);

// Which one fits the supplier in front of you. The reviewer can override it —
// this only decides what is selected when the panel opens, so the common case
// costs no thought and the uncommon one costs one click.
export function suggestEmailType(row, journey) {
  if (!row) return OUTREACH_EMAIL_TYPES[0].key;
  if (journey?.hasSubmitted) return 'notice_supplier_under_review';
  // nudge_confirm_email is offered in the list but never auto-suggested: whether
  // the address is confirmed lives on auth.users, which the verification queue
  // does not read. Guessing it from profiles would be a guess, so the reviewer
  // picks that one deliberately or not at all.
  return 'nudge_supplier_upload_docs';
}

const WA_TEXT = {
  ar: ({ name, missing }) =>
    `مرحبًا ${name || ''}،\nحسابك في مَعبر غير موثّق حتى الآن${missing ? `، والناقص: ${missing}` : ''}.\n`
    + `أكمل ملفك من لوحة المورّد ليصلك طلبات التجار.\nhttps://maabar.io/dashboard`,
  en: ({ name, missing }) =>
    `Hello ${name || ''},\nYour Maabar account is not verified yet${missing ? `. Still missing: ${missing}` : ''}.\n`
    + `Complete your file in the supplier dashboard so trader requests reach you.\nhttps://maabar.io/dashboard`,
  zh: ({ name, missing }) =>
    `您好 ${name || ''}，\n您的 Maabar 账户尚未通过认证${missing ? `，仍缺少：${missing}` : ''}。\n`
    + `请在供应商控制台完善资料，以便接收买家询盘。\nhttps://maabar.io/dashboard`,
};

// The supplier's own language, not the admin's — this message is for him.
function supplierLang(row) {
  const raw = String(row?.lang || '').toLowerCase();
  if (raw.startsWith('zh')) return 'zh';
  if (raw.startsWith('ar')) return 'ar';
  return 'en';
}

export function buildWhatsAppMessage(row, completeness) {
  const lang = supplierLang(row);
  const name = String(row?.company_name || row?.full_name || '').trim();
  const missingItems = (completeness?.items || []).filter((i) => !i.ok);
  // The admin console's REQUIRED_FIELDS carries ar and en only, so naming the
  // fields from there put ENGLISH field names inside a Chinese sentence — for the
  // audience this message exists for. The trilingual labels are the same eight
  // keys the supplier's own dashboard card uses, so both surfaces name a missing
  // field with the same word.
  const label = (item) => {
    const row3 = SUPPLIER_VERIFICATION_FIELD_LABELS[item.key];
    if (row3) return row3[lang] || row3.en;
    return lang === 'ar' ? item.ar : item.en;
  };
  const missing = missingItems.map(label).join(lang === 'zh' ? '、' : lang === 'ar' ? '، ' : ', ');
  return (WA_TEXT[lang] || WA_TEXT.en)({ name, missing });
}

// wa.me wants digits only — no +, no spaces, no dashes.
export function whatsappHref(rawNumber, message) {
  const digits = String(rawNumber || '').replace(/\D/g, '');
  if (!digits) return '';
  return `https://wa.me/${digits}?text=${encodeURIComponent(message)}`;
}

export function supplierWhatsAppNumber(row) {
  const candidate = String(row?.whatsapp || '').trim() || String(row?.phone || '').trim();
  return candidate.replace(/\D/g, '') ? candidate : '';
}

/**
 * Send one outreach message and record it.
 *
 * Returns { ok, channel, href? }. For 'whatsapp' the caller opens `href` — this
 * function does not send it, and says so rather than implying delivery.
 */
export async function sendOutreach({ channel, row, emailType, message, actorId }) {
  if (channel === 'whatsapp') {
    const href = whatsappHref(supplierWhatsAppNumber(row), message);
    if (!href) return { ok: false, channel, error: 'no_number' };
    // Logged as OPENED, not sent: the admin still has to press send in WhatsApp,
    // and a log that claims delivery it cannot observe is worse than no log.
    await logAdminAction({
      actorId,
      action: 'supplier_outreach_whatsapp_opened',
      entityType: 'supplier',
      entityId: row.id,
      notes: message.slice(0, 500),
    });
    return { ok: true, channel, href };
  }

  const { data: session } = await sb.auth.getSession();
  const token = session?.session?.access_token;
  if (!token) return { ok: false, channel, error: 'no_session' };

  let res;
  try {
    res = await fetch(`${SUPABASE_URL}/functions/v1/admin-send-email`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      // The field is `userIds` — read off the function, not assumed. It re-reads
      // every id from the database before sending, so this list only narrows.
      body: JSON.stringify({ type: emailType, userIds: [row.id] }),
    });
  } catch (e) {
    // Security rule 4: a provider call that fails must say so in the log, with
    // the provider's own words, not just return false to the caller.
    console.error('[sendOutreach] admin-send-email unreachable:', e?.message || e);
    return { ok: false, channel, error: 'network' };
  }

  const body = await res.json().catch(() => ({}));
  // It answers 200 with ok:false when some recipients failed, so res.ok alone is
  // not the test — that shape is exactly how an outage hides inside a success.
  const perRecipientError = Array.isArray(body?.results)
    ? body.results.find((r) => r && r.ok === false)?.error
    : null;
  if (!res.ok || body?.ok === false) {
    console.error('[sendOutreach] admin-send-email failed:', res.status,
      JSON.stringify(body?.error || perRecipientError || body));
    return { ok: false, channel, error: body?.error || perRecipientError || `http_${res.status}` };
  }

  await logAdminAction({
    actorId,
    action: 'supplier_outreach_email_sent',
    entityType: 'supplier',
    entityId: row.id,
    notes: emailType,
  });
  return { ok: true, channel, sent: body?.sent ?? 0 };
}

const OUTREACH_ACTIONS = new Set(['supplier_outreach_email_sent', 'supplier_outreach_whatsapp_opened']);

// "Have we already chased this one?" — read off the same review history the file
// already loads, so it costs no extra request.
export function lastOutreach(history = []) {
  return (history || []).find((h) => OUTREACH_ACTIONS.has(h.action)) || null;
}
